import HttpService from './http-service';
import cookieStorage from './cookie-storage';
import { SERVER_URL } from '../constants/api';

class SnapshotCaptureService {
  constructor() {
    this.captureInterval = null;
    this.isCapturing = false;
  }

  /**
   * Start capturing snapshots from a video element
   * @param {HTMLVideoElement} videoElement - The video element to capture from
   * @param {string} channelName - The channel name for the stream
   * @param {number} intervalMs - Capture interval in milliseconds (default 30 seconds)
   */
  startCapturing(videoElement, channelName, intervalMs = 30000) {
    if (this.isCapturing) {
      console.log('[SnapshotCapture] Already capturing');
      return;
    }

    if (!videoElement || !channelName) {
      console.error('[SnapshotCapture] Missing video element or channel name');
      return;
    }

    this.isCapturing = true;
    console.log(`[SnapshotCapture] Starting capture for channel ${channelName}`);

    // Capture immediately
    this.captureSnapshot(videoElement, channelName);

    // Set up interval for periodic captures
    this.captureInterval = setInterval(() => {
      this.captureSnapshot(videoElement, channelName);
    }, intervalMs);
  }

  /**
   * Stop capturing snapshots
   */
  stopCapturing() {
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
      this.captureInterval = null;
    }
    this.isCapturing = false;
    console.log('[SnapshotCapture] Stopped capturing');
  }

  /**
   * Capture a single snapshot from video element
   * @param {HTMLVideoElement} videoElement - The video element to capture from
   * @param {string} channelName - The channel name for the stream
   */
  async captureSnapshot(videoElement, channelName) {
    try {
      // Validate video element
      if (!videoElement || videoElement.readyState < 2) {
        console.warn('[SnapshotCapture] Video element not ready');
        return;
      }

      const width = videoElement.videoWidth || 1280;
      const height = videoElement.videoHeight || 720;
      
      console.log(`[SnapshotCapture] Capturing snapshot: ${width}x${height} for channel ${channelName}`);

      // Create canvas to capture video frame
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      
      // Convert to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.8);
      });

      if (!blob) {
        console.error('[SnapshotCapture] Failed to create blob');
        return;
      }

      console.log(`[SnapshotCapture] Created blob of size: ${blob.size}`);

      // Send to backend
      const formData = new FormData();
      formData.append('channelName', channelName);
      formData.append('snapshot', blob, `snapshot-${Date.now()}.jpg`);
      
      // Debug logging
      console.log('[SnapshotCapture] FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      // Use HttpService directly to avoid JSON content-type
      const token = cookieStorage.getCookie("Authorization");
      const config = {
        headers: {
          Authorization: token
          // Let browser set Content-Type with boundary
        },
        withCredentials: true
      };

      await HttpService.post(`${SERVER_URL}/snapshot/upload`, formData, config);

      console.log(`[SnapshotCapture] Snapshot uploaded for channel ${channelName}`);
    } catch (error) {
      console.error('[SnapshotCapture] Error capturing snapshot:', error);
    }
  }
}

export default new SnapshotCaptureService();